import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X, Trash2, DollarSign, TrendingUp, Edit3, GripVertical, UserCheck, UserPlus, Activity, MessageCircle } from 'lucide-react';
import { crmApi, Funil, FunilEtapa, Negocio, Etiqueta, Contato } from '../crmApi';
import EtiquetasPicker from '../EtiquetasPicker';
import EmpresaContaPicker from '../EmpresaContaPicker';
import ResponsavelPicker from '../ResponsavelPicker';
import CardDrawer from '../CardDrawer';
import CamposCustomForm from '../CamposCustomForm';

interface Props { session: any; }

const CORES = ['#94a3b8', '#3b82f6', '#22c55e', '#eab308', '#f97316', '#ef4444', '#a855f7', '#ec4899', '#14b8a6'];

const CRMPipeline = ({ session }: Props) => {
  const [funis, setFunis] = useState<Funil[]>([]);
  const [funilSelecionadoId, setFunilSelecionadoId] = useState<string>('');
  const [negocios, setNegocios] = useState<Negocio[]>([]);
  const [etiquetasMap, setEtiquetasMap] = useState<Record<string, string>>({});
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [showNovoFunil, setShowNovoFunil] = useState(false);
  const [showEditarFunil, setShowEditarFunil] = useState(false);
  const [showNovoNegocio, setShowNovoNegocio] = useState<{ etapaId: string } | null>(null);
  const [editandoNegocio, setEditandoNegocio] = useState<Negocio | null>(null);
  const [drawerNegocio, setDrawerNegocio] = useState<Negocio | null>(null);
  const [drawerAba, setDrawerAba] = useState<'timeline' | 'whatsapp'>('timeline');
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const funilSelecionado = useMemo(() => funis.find(f => f.id === funilSelecionadoId) || null, [funis, funilSelecionadoId]);

  const carregar = async () => {
    setCarregando(true); setErro(null);
    try {
      const [lista, ets] = await Promise.all([
        crmApi.listarFunis(session),
        crmApi.listarEtiquetas(session),
      ]);
      setFunis(lista);
      const map: Record<string, string> = {};
      ets.forEach((e: Etiqueta) => { map[e.nome] = e.cor; });
      setEtiquetasMap(map);
      if (lista.length > 0 && !funilSelecionadoId) setFunilSelecionadoId(lista[0].id);
    } catch (e: any) {
      setErro(e.message || 'Erro ao carregar funis');
    } finally { setCarregando(false); }
  };

  const carregarNegocios = async (funilId: string) => {
    if (!funilId) { setNegocios([]); return; }
    try {
      const lista = await crmApi.listarNegocios(session, funilId);
      setNegocios(lista);
    } catch (e: any) {
      console.error('Erro listar negócios:', e);
    }
  };

  useEffect(() => { carregar(); }, []); // eslint-disable-line
  useEffect(() => { if (funilSelecionadoId) carregarNegocios(funilSelecionadoId); }, [funilSelecionadoId]); // eslint-disable-line

  const handleDrop = async (etapaId: string) => {
    if (!draggingId) return;
    const negocio = negocios.find(n => n.id === draggingId);
    if (!negocio || negocio.etapaId === etapaId) { setDraggingId(null); return; }
    const updated = negocios.map(n => n.id === draggingId ? { ...n, etapaId } : n);
    setNegocios(updated);
    setDraggingId(null);
    try {
      await crmApi.atualizarNegocio(session, draggingId, { etapaId });
    } catch (e: any) {
      alert('Erro ao mover negócio: ' + e.message);
      carregarNegocios(funilSelecionadoId);
    }
  };

  const negociosPorEtapa = (etapaId: string) => negocios.filter(n => n.etapaId === etapaId);
  const totalEtapa = (etapaId: string) => negociosPorEtapa(etapaId).reduce((s, n) => s + (n.valor || 0), 0);
  const valorTotal = negocios.reduce((s, n) => s + (n.valor || 0), 0);

  return (
    <div className="flex flex-col h-full p-4 sm:p-6 max-w-full overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Funis de Vendas</h1>
          <p className="text-sm text-gray-600">Gerencie seus funis e oportunidades de negócio</p>
        </div>
        <div className="flex items-center gap-2">
          {funis.length > 0 && (
            <select
              value={funilSelecionadoId}
              onChange={(e) => setFunilSelecionadoId(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
            >
              {funis.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          )}
          {funilSelecionado && (
            <button onClick={() => setShowEditarFunil(true)} title="Editar funil"
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Edit3 className="h-4 w-4" />
            </button>
          )}
          <button onClick={() => setShowNovoFunil(true)}
            className="bg-black text-white px-3 py-2 rounded-lg flex items-center gap-1.5 hover:bg-gray-800 text-sm">
            <Plus className="h-4 w-4" /> Novo Funil
          </button>
        </div>
      </div>

      {erro && <div className="mb-4 px-4 py-2 bg-red-50 text-red-700 text-sm rounded-lg">{erro}</div>}
      {carregando && <div className="text-sm text-gray-500">Carregando…</div>}

      {!carregando && funis.length === 0 && (
        <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-300">
          <p className="text-gray-600 mb-4">Você ainda não tem nenhum funil de vendas.</p>
          <button onClick={() => setShowNovoFunil(true)}
            className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800">
            Criar meu primeiro funil
          </button>
        </div>
      )}

      {funilSelecionado && (
        <>
          {/* Métricas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="bg-white p-3 border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-600">Total no Pipeline</p>
              <p className="text-lg font-bold text-gray-900">R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="bg-white p-3 border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-600">Oportunidades</p>
              <p className="text-lg font-bold text-gray-900">{negocios.length}</p>
            </div>
            <div className="bg-white p-3 border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-600">Ticket Médio</p>
              <p className="text-lg font-bold text-gray-900">R$ {(negocios.length ? valorTotal / negocios.length : 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="bg-white p-3 border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-600">Etapas</p>
              <p className="text-lg font-bold text-gray-900">{funilSelecionado.etapas.length}</p>
            </div>
          </div>

          {/* Kanban */}
          <div className="flex gap-3 overflow-x-auto crm-pipeline-scroll flex-1 pb-2">
            {funilSelecionado.etapas.map(etapa => (
              <div key={etapa.id} className="flex-shrink-0 w-72 bg-gray-50 rounded-xl border border-gray-200 flex flex-col max-h-full"
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(etapa.id)}>
                <div className="p-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ borderTopLeftRadius: 12, borderTopRightRadius: 12, backgroundColor: etapa.cor + '20' }}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: etapa.cor }} />
                      <h3 className="font-semibold text-gray-900 text-sm truncate">{etapa.nome}</h3>
                    </div>
                    <p className="text-[11px] text-gray-600 mt-0.5">{negociosPorEtapa(etapa.id).length} • R$ {totalEtapa(etapa.id).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <button onClick={() => setShowNovoNegocio({ etapaId: etapa.id })}
                    className="p-1 hover:bg-white/60 rounded-md text-gray-700">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                <div className="p-2 space-y-2 overflow-y-auto kanban-column-scroll flex-1 min-h-[120px]">
                  {negociosPorEtapa(etapa.id).map(n => (
                    <div key={n.id}
                      draggable
                      onDragStart={() => setDraggingId(n.id)}
                      onDragEnd={() => setDraggingId(null)}
                      onClick={() => setEditandoNegocio(n)}
                      className={`bg-white rounded-lg border border-gray-200 p-2.5 cursor-pointer hover:shadow-md transition-shadow ${draggingId === n.id ? 'opacity-50' : ''}`}>
                      <div className="flex items-start gap-1">
                        <GripVertical className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-1">
                            <p className="font-medium text-sm text-gray-900 truncate flex-1">{n.titulo}</p>
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              {n.telefone && (
                                <button onClick={(e) => {
                                    e.stopPropagation();
                                    window.dispatchEvent(new CustomEvent('crm-open-inbox', { detail: { numero: n.telefone } }));
                                  }}
                                  title="Abrir conversa WhatsApp"
                                  className="p-0.5 hover:bg-green-50 rounded text-green-600">
                                  <MessageCircle className="h-3.5 w-3.5" />
                                </button>
                              )}
                              <button onClick={(e) => { e.stopPropagation(); setDrawerAba('timeline'); setDrawerNegocio(n); }}
                                title="Atividades / Timeline"
                                className="p-0.5 hover:bg-blue-50 rounded text-blue-600">
                                <Activity className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                          {n.cliente && <p className="text-[11px] text-gray-600 truncate">{n.cliente}</p>}
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-xs font-semibold text-green-700 flex items-center gap-0.5">
                              <DollarSign className="h-3 w-3" />
                              {(n.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                            {(n.etiquetas || []).length > 0 && (
                              <div className="flex gap-0.5 flex-wrap">
                                {(n.etiquetas || []).slice(0, 2).map((nome, i) => {
                                  const cor = etiquetasMap[nome] || '#6b7280';
                                  return (
                                    <span key={i}
                                      className="text-[9px] px-1.5 py-0.5 rounded-full text-white"
                                      style={{ backgroundColor: cor }}>
                                      {nome}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {negociosPorEtapa(etapa.id).length === 0 && (
                    <div className="text-center py-8 text-xs text-gray-400">
                      Arraste oportunidades para cá
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {showNovoFunil && (
        <FunilModal session={session} onClose={() => setShowNovoFunil(false)}
          onSaved={async (id) => { await carregar(); setFunilSelecionadoId(id); setShowNovoFunil(false); }} />
      )}

      {showEditarFunil && funilSelecionado && (
        <FunilEditarModal session={session} funil={funilSelecionado}
          onClose={() => setShowEditarFunil(false)}
          onSaved={async () => { await carregar(); setShowEditarFunil(false); }}
          onRemoved={async () => {
            if (!confirm(`Excluir o funil "${funilSelecionado.nome}" e todos os seus negócios?`)) return;
            await crmApi.removerFunil(session, funilSelecionado.id);
            setFunilSelecionadoId('');
            await carregar();
            setShowEditarFunil(false);
          }} />
      )}

      {showNovoNegocio && funilSelecionado && (
        <NegocioModal session={session} funilId={funilSelecionado.id}
          etapaId={showNovoNegocio.etapaId} etapas={funilSelecionado.etapas}
          onClose={() => setShowNovoNegocio(null)}
          onSaved={async () => { await carregarNegocios(funilSelecionado.id); setShowNovoNegocio(null); }} />
      )}

      {editandoNegocio && funilSelecionado && (
        <NegocioModal session={session} funilId={funilSelecionado.id}
          etapaId={editandoNegocio.etapaId} etapas={funilSelecionado.etapas}
          negocio={editandoNegocio}
          onClose={() => setEditandoNegocio(null)}
          onSaved={async () => { await carregarNegocios(funilSelecionado.id); setEditandoNegocio(null); }}
          onRemoved={async () => {
            if (!confirm('Excluir esta oportunidade?')) return;
            await crmApi.removerNegocio(session, editandoNegocio.id);
            await carregarNegocios(funilSelecionado.id);
            setEditandoNegocio(null);
          }} />
      )}

      <CardDrawer session={session} open={!!drawerNegocio} onClose={() => setDrawerNegocio(null)}
        vinculo={drawerNegocio ? { negocioId: drawerNegocio.id, contatoId: drawerNegocio.contatoId || undefined, empresaContaId: drawerNegocio.empresaContaId || undefined } : {}}
        titulo={drawerNegocio?.titulo || ''}
        subtitulo={drawerNegocio?.cliente || ''}
        telefone={drawerNegocio?.telefone}
        initialAba={drawerAba}
        responsavelId={(drawerNegocio as any)?.responsavelId} />
    </div>
  );
};

// =============== Modais ===============

const FunilModal = ({ session, onClose, onSaved }: { session: any; onClose: () => void; onSaved: (id: string) => void }) => {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const salvar = async () => {
    if (!nome.trim()) { alert('Informe o nome do funil'); return; }
    setSalvando(true);
    try {
      const f = await crmApi.criarFunil(session, nome.trim(), descricao.trim());
      onSaved(f.id);
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setSalvando(false); }
  };
  return (
    <Modal title="Novo Funil" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-gray-600">Nome *</label>
          <input value={nome} onChange={e => setNome(e.target.value)} autoFocus
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Ex: Vendas B2B" />
        </div>
        <div>
          <label className="text-xs text-gray-600">Descrição</label>
          <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows={2}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <p className="text-[11px] text-gray-500">O funil será criado com 6 etapas padrão (Lead, Qualificação, Proposta, Negociação, Fechado, Perdido). Você pode personalizá-las depois.</p>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-700">Cancelar</button>
        <button onClick={salvar} disabled={salvando}
          className="px-4 py-1.5 bg-black text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50">
          {salvando ? 'Salvando...' : 'Criar Funil'}
        </button>
      </div>
    </Modal>
  );
};

const FunilEditarModal = ({ session, funil, onClose, onSaved, onRemoved }: { session: any; funil: Funil; onClose: () => void; onSaved: () => void; onRemoved: () => void }) => {
  const [nome, setNome] = useState(funil.nome);
  const [descricao, setDescricao] = useState(funil.descricao || '');
  const [etapas, setEtapas] = useState<FunilEtapa[]>(funil.etapas);
  const [salvando, setSalvando] = useState(false);

  const addEtapa = () => {
    setEtapas([...etapas, { id: 'tmp_' + Date.now(), nome: 'Nova etapa', cor: CORES[etapas.length % CORES.length], probabilidade: 50 }]);
  };
  const updEtapa = (idx: number, patch: Partial<FunilEtapa>) => {
    setEtapas(etapas.map((e, i) => i === idx ? { ...e, ...patch } : e));
  };
  const rmEtapa = (idx: number) => { setEtapas(etapas.filter((_, i) => i !== idx)); };
  const moveEtapa = (idx: number, dir: -1 | 1) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= etapas.length) return;
    const cp = [...etapas]; [cp[idx], cp[ni]] = [cp[ni], cp[idx]];
    setEtapas(cp);
  };
  const salvar = async () => {
    if (!nome.trim()) { alert('Nome obrigatório'); return; }
    if (etapas.length === 0) { alert('Pelo menos uma etapa é obrigatória'); return; }
    setSalvando(true);
    try {
      await crmApi.atualizarFunil(session, funil.id, { nome: nome.trim(), descricao, etapas });
      onSaved();
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setSalvando(false); }
  };

  return (
    <Modal title="Editar Funil" onClose={onClose} large>
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600">Nome</label>
            <input value={nome} onChange={e => setNome(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-600">Descrição</label>
            <input value={descricao} onChange={e => setDescricao(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
        </div>

        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-sm text-gray-900">Etapas do funil</h4>
            <button onClick={addEtapa} className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center gap-1">
              <Plus className="h-3 w-3" /> Adicionar etapa
            </button>
          </div>
          <div className="space-y-2">
            {etapas.map((e, idx) => (
              <div key={e.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex flex-col">
                  <button onClick={() => moveEtapa(idx, -1)} className="text-[10px] text-gray-500 hover:text-black">▲</button>
                  <button onClick={() => moveEtapa(idx, 1)} className="text-[10px] text-gray-500 hover:text-black">▼</button>
                </div>
                <input value={e.nome} onChange={ev => updEtapa(idx, { nome: ev.target.value })}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded" placeholder="Nome da etapa" />
                <input type="color" value={e.cor} onChange={ev => updEtapa(idx, { cor: ev.target.value })}
                  className="w-8 h-8 border border-gray-300 rounded cursor-pointer" title="Cor" />
                <input type="number" min={0} max={100} value={e.probabilidade}
                  onChange={ev => updEtapa(idx, { probabilidade: Number(ev.target.value) })}
                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded" title="Probabilidade %" />
                <button onClick={() => rmEtapa(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-between gap-2 mt-5">
        <button onClick={onRemoved} className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg">Excluir funil</button>
        <div className="flex gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-700">Cancelar</button>
          <button onClick={salvar} disabled={salvando}
            className="px-4 py-1.5 bg-black text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const NegocioModal = ({ session, funilId, etapaId, etapas, negocio, onClose, onSaved, onRemoved }: {
  session: any; funilId: string; etapaId: string; etapas: FunilEtapa[]; negocio?: Negocio;
  onClose: () => void; onSaved: () => void; onRemoved?: () => void;
}) => {
  const [titulo, setTitulo] = useState(negocio?.titulo || '');
  const [valor, setValor] = useState<string>(negocio?.valor != null ? String(negocio.valor) : '');
  const [cliente, setCliente] = useState(negocio?.cliente || '');
  const [email, setEmail] = useState(negocio?.email || '');
  const [telefone, setTelefone] = useState(negocio?.telefone || '');
  const [observacoes, setObservacoes] = useState(negocio?.observacoes || '');
  const [etapa, setEtapa] = useState(negocio?.etapaId || etapaId);
  const [origem, setOrigem] = useState(negocio?.origem || '');
  const [etiquetas, setEtiquetas] = useState<string[]>(negocio?.etiquetas || []);
  const [camposCustom, setCamposCustom] = useState<Record<string, any>>(negocio?.camposCustom || {});
  const [salvando, setSalvando] = useState(false);

  // ── Autocomplete de contato (Cliente) ──
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [showSugestoes, setShowSugestoes] = useState(false);
  const [contatoExistenteId, setContatoExistenteId] = useState<string | null>(negocio?.contatoId || null);
  const [empresaContaId, setEmpresaContaId] = useState<string | null>(negocio?.empresaContaId || null);
  const [responsavelId, setResponsavelId] = useState<string | null>(negocio?.responsavelId || (session?.usuario?.email || null));
  const [salvarComoContato, setSalvarComoContato] = useState(false);
  const clienteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    crmApi.listarContatos(session).then(setContatos).catch(() => {});
  }, []); // eslint-disable-line

  // Fechar sugestões ao clicar fora
  useEffect(() => {
    const onClick = (ev: MouseEvent) => {
      if (clienteRef.current && !clienteRef.current.contains(ev.target as Node)) setShowSugestoes(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const sugestoes = useMemo(() => {
    if (!cliente || cliente.length < 2 || contatoExistenteId) return [];
    const termo = cliente.toLowerCase();
    return contatos.filter(c =>
      c.nome?.toLowerCase().includes(termo) ||
      c.empresa?.toLowerCase().includes(termo) ||
      c.email?.toLowerCase().includes(termo)
    ).slice(0, 6);
  }, [cliente, contatos, contatoExistenteId]);

  const selecionarContato = (c: Contato) => {
    setCliente(c.nome);
    setEmail(c.email || '');
    setTelefone(c.telefone || '');
    setContatoExistenteId(c.id);
    if (c.empresaContaId) setEmpresaContaId(c.empresaContaId);
    setShowSugestoes(false);
    setSalvarComoContato(false);
  };

  const limparContatoSelecionado = () => {
    setContatoExistenteId(null);
    setCliente('');
    setEmail('');
    setTelefone('');
  };

  const salvar = async () => {
    if (!titulo.trim()) { alert('Título obrigatório'); return; }
    setSalvando(true);
    try {
      // Se marcado, cria contato novo antes de salvar a oportunidade
      let contatoIdFinal = contatoExistenteId;
      if (salvarComoContato && !contatoExistenteId && cliente.trim()) {
        try {
          const novo = await crmApi.criarContato(session, {
            nome: cliente.trim(), email, telefone, tipo: 'cliente',
          } as any);
          contatoIdFinal = (novo as any)?.id || null;
        } catch (err) {
          console.warn('Não foi possível criar contato:', err);
        }
      }
      const payload: Partial<Negocio> = {
        funilId, etapaId: etapa,
        titulo: titulo.trim(),
        valor: parseFloat(valor.replace(',', '.')) || 0,
        cliente, email, telefone, observacoes, origem, etiquetas, camposCustom,
        contatoId: contatoIdFinal || undefined,
        empresaContaId: empresaContaId || undefined,
        responsavelId: responsavelId || undefined,
      } as any;
      if (negocio) {
        await crmApi.atualizarNegocio(session, negocio.id, payload);
      } else {
        await crmApi.criarNegocio(session, payload);
      }
      onSaved();
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setSalvando(false); }
  };

  return (
    <Modal title={negocio ? 'Editar Oportunidade' : 'Nova Oportunidade'} onClose={onClose}>
      <div className="space-y-3">
        {/* Linha 1: Título (full) */}
        <div>
          <label className="text-xs text-gray-600">Título *</label>
          <input value={titulo} onChange={e => setTitulo(e.target.value)} autoFocus
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Ex: Venda software ACME" />
        </div>
        {/* Linha 2: Valor | Etapa | Origem */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-gray-600">Valor (R$)</label>
            <input value={valor} onChange={e => setValor(e.target.value)} inputMode="decimal"
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0,00" />
          </div>
          <div>
            <label className="text-xs text-gray-600">Etapa</label>
            <select value={etapa} onChange={e => setEtapa(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
              {etapas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Origem</label>
            <input value={origem} onChange={e => setOrigem(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Indicação, Site…" />
          </div>
        </div>
        {/* Linha 3: Cliente | E-mail | Telefone */}
        <div className="grid grid-cols-3 gap-3">
          <div className="relative" ref={clienteRef}>
            <div className="flex items-center justify-between">
              <label className="text-xs text-gray-600">Cliente</label>
              {contatoExistenteId && (
                <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                  <UserCheck className="h-2.5 w-2.5" /> contato existente
                  <button onClick={limparContatoSelecionado} className="ml-0.5 hover:bg-green-200 rounded"><X className="h-2.5 w-2.5" /></button>
                </span>
              )}
            </div>
            <input value={cliente}
              onChange={e => { setCliente(e.target.value); setContatoExistenteId(null); setShowSugestoes(true); }}
              onFocus={() => setShowSugestoes(true)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Digite para buscar ou criar..." autoComplete="off" />
            {showSugestoes && sugestoes.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-56 overflow-y-auto">
                {sugestoes.map(c => (
                  <button key={c.id} onClick={() => selecionarContato(c)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{c.nome}</div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {c.empresa ? `${c.empresa} · ` : ''}{c.email || c.telefone || ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {!contatoExistenteId && cliente.trim().length >= 2 && sugestoes.length === 0 && (
              <label className="flex items-center gap-1.5 mt-1 cursor-pointer text-[11px] text-gray-600 hover:text-gray-800">
                <input type="checkbox" checked={salvarComoContato} onChange={e => setSalvarComoContato(e.target.checked)}
                  className="h-3 w-3 rounded border-gray-300" />
                <UserPlus className="h-3 w-3" /> Cadastrar como novo contato
              </label>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-600">E-mail</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email"
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-600">Telefone</label>
            <input value={telefone} onChange={e => setTelefone(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
        </div>
        {/* Linha 4: Observações | Etiquetas lado a lado */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600">Observações</label>
            <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={3}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <EtiquetasPicker session={session} value={etiquetas} onChange={setEtiquetas} />
          </div>
        </div>
        {/* Empresa (Conta) */}
        <EmpresaContaPicker session={session} value={empresaContaId} onChange={(id) => setEmpresaContaId(id)} />
        <ResponsavelPicker session={session} value={responsavelId} onChange={setResponsavelId} />
        <CamposCustomForm session={session} entidade="negocio" value={camposCustom} onChange={setCamposCustom} />
      </div>
      <div className="flex justify-between gap-2 mt-5">
        {onRemoved ? (
          <button onClick={onRemoved} className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg">Excluir</button>
        ) : <span />}
        <div className="flex gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-700">Cancelar</button>
          <button onClick={salvar} disabled={salvando}
            className="px-4 py-1.5 bg-black text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Modal = ({ title, onClose, children, large }: { title: string; onClose: () => void; children: React.ReactNode; large?: boolean }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3">
    <div className={`bg-white rounded-2xl shadow-xl ${large ? 'w-full max-w-4xl' : 'w-full max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="h-4 w-4" /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

export default CRMPipeline;
